import { reactive, readonly } from 'vue';
import router from '@/router';
import axios from 'axios';
import Swal from 'sweetalert2';
import * as jose from "jose";

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: false
});

const state = reactive({
  loading: false,
  error: null,
});

// Same shape the screens use for their own toasts, with a longer timer: an
// explanation takes more reading than "Saved".
const errorToast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 5000
});

axiosInstance.interceptors.request.use(async (config) => {
  const token = localStorage.getItem('token');

  if (token) {
    let decoded = jose.decodeJwt(token);
    const currentTime = Math.floor(Date.now() / 1000);

    if (decoded.exp < currentTime) {
      localStorage.removeItem('token');
      await router.push('/login');
      return Promise.reject('Session Expired');
    }

    config.headers.Authorization = 'Bearer ' + token;
  }

  state.loading = true;
  return config;
}, (error) => {
  state.loading = false;
  state.error = error;
  return Promise.reject(error);
});

axiosInstance.interceptors.response.use((response) => {
  state.loading = false;
  return response;
}, async (error) => {
  state.loading = false;
  state.error = error;
  // Handle 404 error
  if (error.response && error.response.status === 404) {
    await router.push('/error/404')
  }
  // Handle 401 error
  if (error.response && error.response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('usr');
    await router.push('/login');
  }

  // Say what went wrong. Every error the API returns carries { message }, and
  // nothing was rendering it: the screens are written as
  // `.then(res => { if (res.data.updated) ... })` with no rejection handler, so a
  // refused request looked exactly like nothing happening. A status change the
  // pricing table cannot price answers 422 with the reason, and the operator saw
  // a button that span and stopped.
  //
  // 401 and 404 are handled above by navigating, and a toast on top of a redirect
  // is noise. `skipErrorToast` on the request opts out the few callers that show
  // their own message, so they do not show two.
  const status = error.response?.status;
  const message = error.response?.data?.message;
  if (message && status !== 401 && status !== 404 && !error.config?.skipErrorToast) {
    errorToast.fire({ icon: 'error', title: message });
  }

  return Promise.reject(error);
});

export { axiosInstance };

export const axiosPlugin = {
  install: (app) => {
    app.config.globalProperties.$axios = axiosInstance;
    app.provide('axios', axiosInstance);
    app.config.globalProperties.$httpState = readonly(state);
  },
};
